import { Check } from "@mui/icons-material";
import { Card, CardActions, CardContent, CardHeader, Chip, Grid, Typography } from "@mui/material";
import { useState } from "react";
import { useSubscription } from "../../../../Hooks/useSubscription";
import { useToast } from "../../../../Hooks/useToast";
import { useSubscriptionRequestMutation } from "../../../../Redux/api/stripeApi";
import { KaziButton } from "../../../../Shared Components/Kazi/KaziButton";
import { SubscriptionTier } from "../../../../Types/Client";
import { ISubscription } from "../../../../Types/subcription";
import { EnterpriseUpgradeModal } from "./EnterpriseUpgradeModal";
import StripeCheckoutDialog from "./StripeCheckoutDialog";
import { useUpdateSubscriptionMutation } from "../../../../Redux/api/subscriptionApi";
import { useSearchParams } from "react-router-dom";
import { useUser } from "../../../../Hooks/useUser";
import { AppSettings } from "../../../../Utils/Settings";

interface ISubscriptionCardProps {
    subscription: ISubscription;
    currentPlan: boolean;
    isMonthly: boolean;
}

export const SubscriptionCard = ({ subscription, currentPlan = false, isMonthly }: ISubscriptionCardProps) => {
    /* ******************** Hooks ******************** */
    const { showToast } = useToast();
    const { user, invalidateUser } = useUser();
    const { formatSubscriptionPrice } = useSubscription();
    const [ subscriptionRequest, { isLoading: requestLoading } ] = useSubscriptionRequestMutation();
    const [ updateSubscription, { isLoading: updateLoading }] = useUpdateSubscriptionMutation();
    /* ******************** Variables ******************** */
    const [enterpriseModalopen, setEnterpriseModalOpen] = useState(false);
    const [result, setResult] = useState<any>('');
    const [openCheckout, setOpenCheckout] = useState(false);
    const [searchParams, setSearchParams ] = useSearchParams();
    /* ******************** Functions ******************** */
    const handleCheckout = (price_id: string) => {
      subscriptionRequest({
        price_id
      })
      .unwrap()
      .then(res => {
        setResult(res.clientSecret);
        setOpenCheckout(true);
      })
      .catch( err => {
        console.error("Error occured while making stripe subscription request: ", err);
        showToast({
          text: "Unable to select plan",
          variant: "error"
        })
      });
    }
    const handlePlanSelection = async () => {
      switch(true) {
        case subscription.tier == SubscriptionTier.Enterprise:
          setEnterpriseModalOpen(true);
          break;
        case subscription.tier == SubscriptionTier.Premium:
          handleCheckout(isMonthly ? AppSettings.Stripe.MonthlyId : AppSettings.Stripe.AnnualId);
          break;
        case subscription.tier == SubscriptionTier.PremiumAfrica:
          handleCheckout(isMonthly ? AppSettings.Stripe.MonthlyAfricaId : AppSettings.Stripe.AnnualAfricaId);
          break;
      }
    };
    const onComplete = () => {
        updateSubscription({
            locator: user?.locator!,
            body: {
                tier: subscription.tier,
                type: 'monthly'
            }
        })
        .unwrap()
        .then((res) => {
            invalidateUser();
            setSearchParams({mode: 'view'});
        })
        .catch( (err) => {
            console.error('Error ocurred while upgrading: ', err)
            showToast({
                text: "Unable to upgrade subscription",
                variant: 'error'
            })
        })
    }
    
    /* ******************** Effects ******************** */
    /* ******************** JSX ******************** */
    return (
      <>
        <Card
          sx={{
            flex: 1,
            borderRadius: "12px",
            "& .MuiCardHeader-title": {
              fontSize: "22px",
            },
            "& .MuiCardHeader-subheader": {
              fontSize: "12px",
            },
            paddingBottom: "20px",
            paddingX: "10px",
          }}
        >
          <CardHeader
            title={subscription.title}
            action={subscription.tier === SubscriptionTier.Premium || subscription.tier === SubscriptionTier.PremiumAfrica ? <Chip color='warning' label='30 day free-trial'/> : null}
            subheader={formatSubscriptionPrice(subscription.tier, isMonthly)}
            sx={{
              margin: 0,
              paddingBottom: 0,
            }}
          />
          <CardContent
            sx={{
              height: "241px",
              margin: 0,
            }}
          >
            <Grid display={"flex"} flexDirection={"column"} gap={1}>
              {subscription.features.map((f, idx) => (
                <Grid
                  key={subscription.tier + f + idx}
                  display={"flex"}
                  flexDirection={"row"}
                  gap={1}
                >
                  <Check />
                  <Typography variant="body-medium">{f}</Typography>
                </Grid>
              ))}
            </Grid>
          </CardContent>
          <CardActions
            sx={{
              display: "flex",
              flexDirection: "row",
            }}
          >
            <Grid
              flex={1}
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"flex-start"}
            >
              {currentPlan && (
                <Typography variant="body-small">Your current plan</Typography>
              )}
            </Grid>
            <Grid
              flex={1}
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"flex-end"}
            >
              {subscription.tier !== SubscriptionTier.Starter && (
                <KaziButton
                  variant="contained"
                  disabled={currentPlan}
                  onClick={handlePlanSelection}
                  text={
                    subscription.tier == SubscriptionTier.Enterprise
                      ? "Contact"
                      : "Select plan"
                  }
                />
              )}
            </Grid>
          </CardActions>
        </Card>
        <EnterpriseUpgradeModal open={enterpriseModalopen} setOpen={setEnterpriseModalOpen} />
        <StripeCheckoutDialog 
          open={openCheckout} 
          setOpen={setOpenCheckout} 
          clientSecret={result} 
          onComplete={onComplete}
        />
      </>
    );
}